import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

function getRecommendationSeedProductIds(items) {
  const seen = new Set();
  const productIds = [];

  for (const item of [...(items ?? [])].reverse()) {
    if (item.properties?.['_free_gift'] || seen.has(item.product_id)) continue;
    seen.add(item.product_id);
    productIds.push(item.product_id);
  }

  if (productIds.length === 0) {
    const lastItem = items?.[items.length - 1];
    if (lastItem) productIds.push(lastItem.product_id);
  }

  return productIds;
}

function refreshRecommendationsAfterCartUpdate(event) {
  (event.promise ?? Promise.resolve())
    .then((result) => {
      const items = result?.detail?.items;
      if (!items) return;

      document.querySelectorAll('cart-drawer-recommendations').forEach((recommendations) => {
        recommendations.refreshFromCart(items);
      });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer-recommendations] Failed to reload:', error);
    });
}

document.addEventListener(StandardEvents.cartLinesUpdate, refreshRecommendationsAfterCartUpdate);

class CartDrawerRecommendations extends Component {

  #activeFetch = null;

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName !== 'data-product-ids' && mutation.attributeName !== 'data-cart-item-count') continue;

      this.#loadRecommendations();
      break;
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#mutationObserver.observe(this, { attributes: true });
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  refreshFromCart(items) {
    if (!items?.length) return;

    this.dataset.productIds = getRecommendationSeedProductIds(items).join(',');
    this.dataset.cartItemCount = String(items.reduce((sum, item) => sum + item.quantity, 0));
  }

  #loadRecommendations() {
    const { sectionId, intent } = this.dataset;
    const seedProductIds = (this.dataset.productIds ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const maxProducts = Number(this.dataset.maxProducts) || 10;

    if (seedProductIds.length === 0 || !sectionId) return;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    Promise.all(seedProductIds.map((productId) => this.#fetchRecommendations(productId, sectionId, intent, abortController.signal)))
      .then((results) => {
        if (abortController.signal.aborted) return;

        const successfulResults = results.filter((result) => result.success);
        if (successfulResults.length === 0) {
          this.#handleError(new Error('All recommendation requests failed'));
          return;
        }

        const merged = this.#mergeSlides(successfulResults.map((result) => result.data), maxProducts);
        if (merged) {
          morphSection(sectionId, merged, { mode: 'full' });
          document
            .getElementById(`shopify-section-${sectionId}`)
            ?.querySelector('cart-recommended-products-swiper')
            ?.reinit();
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.#handleError(error);
      })
      .finally(() => {
        if (this.#activeFetch === abortController) this.#activeFetch = null;
      });
  }

  #mergeSlides(htmlResponses, maxProducts) {
    const parser = new DOMParser();
    const docs = htmlResponses.map((html) => parser.parseFromString(html, 'text/html'));

    const slidesBySeed = docs.map((doc) => Array.from(doc.querySelectorAll('.cart-recommended-products__slide')));

    const seenSlideIds = new Set();

    const mergedSlides = [];

    let exhausted = false;
    for (let round = 0; !exhausted && mergedSlides.length < maxProducts; round++) {
      exhausted = true;

      for (const seedSlides of slidesBySeed) {
        const slide = seedSlides[round];
        if (!slide) continue;

        exhausted = false;
        if (mergedSlides.length >= maxProducts) break;

        const slideId = slide.dataset.productId ?? slide.id;
        if (slideId && seenSlideIds.has(slideId)) continue;

        seenSlideIds.add(slideId);
        mergedSlides.push(slide);
      }
    }

    const shellDoc = docs[0];
    if (!shellDoc) return null;

    const shellSection = shellDoc.getElementById(`shopify-section-${this.dataset.sectionId}`);
    if (!shellSection) return null;

    const slidesContainer = shellSection.querySelector('.cart-recommended-products__slideshow .swiper-wrapper');
    const wrapper = shellSection.querySelector('.cart-recommended-products');

    if (mergedSlides.length === 0) {
      wrapper?.setAttribute('hidden', '');
      wrapper?.querySelector('cart-recommended-products-swiper')?.remove();
      wrapper?.querySelector('.cart-recommended-products__title')?.remove();
      return shellSection.outerHTML;
    }

    wrapper?.removeAttribute('hidden');

    if (slidesContainer) {
      slidesContainer.replaceChildren(...mergedSlides);
    }

    return shellSection.outerHTML;
  }

  async #fetchRecommendations(productId, sectionId, intent, signal) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    try {
      const response = await fetch(url, { signal, cache: 'no-store' });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      return { success: true, data: text };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { success: false, status: 0 };
    }
  }

  #handleError(error) {
    console.error('Cart drawer recommendations error:', error.message);
    this.dataset.error = 'Error loading cart drawer recommendations';
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
